// The ONE owner of the "בעריכה" editing-lease renewal for the whole Swingies/MiniMove guest flow (face gallery,
// body gallery, edit, result). The face gallery (useFaceClaim) and the later pages (useClaimRenewal) both renew
// through renewStoredHold(), so the throttle, the single request in flight and the "server refused it" marker
// are shared and a route change never sends a second renew.
//
// * Renewed only after REAL activity (the callers' pointerdown / touchstart / keydown listeners), never on a timer,
//   and at most once per RENEW_MIN_MS per token (the attempt time counts, so a failed renew is retried on later
//   activity, not in a loop). The acquire itself starts the lease (noteHoldRenewed). renewClaim bounds the whole
//   exchange (RENEW_TIMEOUT_MS < RENEW_MIN_MS): a hung renew settles as `network` and frees the in-flight slot; the
//   abandoned exchange's late answer is never seen.
// * Never renews an expired / ended flow (expiry-first: the flow hook's capture listener runs first and latches),
//   a paused flow (processing), a token for which an approve was sent (`approveRid`: the claim belongs to the
//   approve now), another guest's record (readClaimState is guest-bound) or a token the server already refused
//   (410 / 409 processing: never revived).
// * A reply is returned only while the same event visit is current AND the stored token is still the one that was
//   renewed: an old reply never touches a newer or cleared claim. The server stays the authority on the lease.
import { readClaimState, renewClaim } from "./faceClaimClient";
import { isFlowEnded, isFlowExpired, isFlowPaused } from "./useFlowInactivity";

export const RENEW_MIN_MS = 15000;

let lastRenew = null; // { scope, token, at }: the lease start (acquire) or the last renew attempt
let inflight = null; // { scope, token }
let refused = null; // { scope, token }: answered 410 / processing, never renewed again

const same = (entry, event, token) => entry !== null && entry.scope === event.scope && entry.token === token;

const storedToken = (event) => (readClaimState(event.eventName) || {}).token || null;

// The acquire just started (or replayed) this token's lease.
export const noteHoldRenewed = (event, token) => {
  if (event && token) lastRenew = { scope: event.scope, token, at: Date.now() };
};

// Renews the stored editing hold of `event` if every guard allows it. Resolves null when nothing was sent or the
// reply no longer belongs to the stored hold, else { token, result } (result as returned by renewClaim).
export const renewStoredHold = async (event) => {
  if (!event || !event.isCurrent()) return null;
  if (isFlowEnded(event) || isFlowExpired() || isFlowPaused()) return null;
  const state = readClaimState(event.eventName);
  const token = (state && state.token) || null;
  if (!token || state.approveRid) return null;
  if (same(refused, event, token) || same(inflight, event, token)) return null;
  if (same(lastRenew, event, token) && Date.now() - lastRenew.at < RENEW_MIN_MS) return null;
  lastRenew = { scope: event.scope, token, at: Date.now() };
  const mine = { scope: event.scope, token };
  inflight = mine;
  let result;
  try {
    result = await renewClaim(event, token);
  } finally {
    if (inflight === mine) inflight = null;
  }
  if (!event.isCurrent() || storedToken(event) !== token) return null;
  if (result.status === 410 || (result.status === 409 && result.code === "processing")) {
    refused = { scope: event.scope, token };
  }
  return { token, result };
};

// Tests only: forget the module state between cases.
export const resetClaimRenewalForTests = () => {
  lastRenew = null;
  inflight = null;
  refused = null;
};
